"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useOptimistic,
  useRef,
  useSyncExternalStore,
  useTransition,
  type ReactNode,
  type RefObject,
} from "react";

import {
  DEPARTURE_FILTER_ATTR,
  DEPARTURE_FILTER_PARAMS,
  INTERACTIVE_HOLD_ATTR,
  INTERACTIVE_RELEASE_EVENT,
  readDepartureFilters,
} from "@tripcart/shared";
import type { ComboboxElement } from "@tripcart/page-builder/web-components";

import { addressWithFilters } from "./departure-address";
import css from "./departure-dates-island.module.css";

/**
 * The script half of a Departure Dates block. The block is server-rendered
 * and works with no script: its form submits by GET and Show more is a link
 * (AC-14). This island makes both a soft navigation instead.
 *
 * The address is the state (design.md D104). A change builds the next address
 * from the form and goes through `router.push`, so Back returns to the
 * previous filters. The route renders the block again on the server, and the
 * transition keeps the current list on screen until the new one is ready
 * (AC-13). `useOptimistic` holds the address being
 * built, so a second change before the first lands composes with it, and it
 * marks the results busy meanwhile.
 *
 * It draws nothing of its own: a `display: contents` wrapper that listens to
 * the events bubbling out of the stored form and list.
 */

const filterSelector = (filter: string) =>
  `[${DEPARTURE_FILTER_ATTR}="${filter}"]`;
/** `DEPARTURE_LIST_CLASS`: the list component sets it; the stored tree
 *  cannot change it. */
const RESULTS_SELECTOR = ".tc-departures__results";
const MORE_SELECTOR = "a.tc-departures__more";

const subscribeNever = () => () => {};

/**
 * The form's fields from the controls on screen alone. Every filter bar
 * stores two copies of year, month and sort: its own, and a phone row of
 * dropdowns, and CSS shows one of them for the screen's width
 * (`departure-filter-headers-css.ts` in page-builder). Both send the same
 * field, so each copy that is not drawn is disabled for the read, which
 * keeps it out of the form, and enabled again at once: a visitor who turns
 * the phone can still use it.
 */
function visibleFormData(form: HTMLFormElement): FormData {
  const hidden = [
    ...form.querySelectorAll<HTMLElement>(`[${DEPARTURE_FILTER_ATTR}]`),
  ].filter((control) => control.getClientRects().length === 0);
  const fields = hidden.flatMap((control) =>
    control.matches("tc-combobox")
      ? [control]
      : [...control.querySelectorAll<HTMLElement>("input")],
  );
  for (const field of fields) field.setAttribute("disabled", "");
  const data = new FormData(form);
  for (const field of fields) field.removeAttribute("disabled");
  return data;
}

function filtersOfForm(form: HTMLFormElement, changed: EventTarget | null) {
  const params = new URLSearchParams();
  for (const [name, value] of visibleFormData(form)) {
    if (typeof value === "string") params.append(name, value);
  }
  // A month belongs to a year: a new year starts on every month.
  if (changed instanceof Element && changed.closest(filterSelector("year"))) {
    params.delete(DEPARTURE_FILTER_PARAMS.month);
  }
  return readDepartureFilters(params);
}

/**
 * After the server answers, the controls show what the address says. The
 * radios are uncontrolled, so they keep the visitor's last click until told;
 * the rendered `defaultChecked` is the server's answer. A combobox keeps its
 * own choice, so each one — the tour picker, and Sort by, the year and the
 * month where a filter bar draws them as dropdowns — is handed its chosen
 * options (a Back, for one).
 */
function showAddressInControls(host: HTMLElement): void {
  for (const radio of host.querySelectorAll<HTMLInputElement>(
    'input[type="radio"]',
  )) {
    radio.checked = radio.defaultChecked;
  }
  for (const picker of host.querySelectorAll<ComboboxElement>(
    `tc-combobox[${DEPARTURE_FILTER_ATTR}]`,
  )) {
    picker.values = [
      ...picker.querySelectorAll<HTMLElement>("[data-value][data-chosen]"),
    ].map((option) => option.dataset.value ?? "");
  }
}

const isPlainClick = (event: MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/**
 * A filter change, a press of Enter in the form, and a Show more click, each
 * as a soft navigation. The form is looked up per event: a new answer can
 * redraw it (a tour picker that appears once there are two tours).
 */
function useBlockEvents(
  ref: RefObject<HTMLDivElement | null>,
  address: string,
  go: (next: string) => void,
): void {
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const formOf = () =>
      host.querySelector<HTMLFormElement>(filterSelector("form"));
    const onChange = (event: Event) => {
      const form = formOf();
      if (!form?.contains(event.target as Node)) return;
      const filters = filtersOfForm(form, event.target);
      go(addressWithFilters(new URLSearchParams(address), filters));
    };
    const onSubmit = (event: Event) => {
      if (event.target !== formOf()) return;
      event.preventDefault();
      onChange(event);
    };
    const onClick = (event: MouseEvent) => {
      const link = (event.target as Element).closest?.(MORE_SELECTOR);
      if (!(link instanceof HTMLAnchorElement) || !isPlainClick(event)) return;
      event.preventDefault();
      go(link.search.slice(1));
    };
    const listeners: [string, EventListener][] = [
      ["change", onChange],
      ["tc-combobox-change", onChange],
      ["submit", onSubmit],
      ["click", onClick as EventListener],
    ];
    for (const [name, listener] of listeners) {
      host.addEventListener(name, listener);
    }
    return () => {
      for (const [name, listener] of listeners) {
        host.removeEventListener(name, listener);
      }
    };
  }, [ref, address, go]);
}

export function DepartureDatesIsland({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const current = searchParams.toString();
  const [address, setAddress] = useOptimistic(current);
  const [, startTransition] = useTransition();
  const ref = useRef<HTMLDivElement>(null);
  // True once hydrated, and never in the server render, so the no-script
  // Apply button is hidden only when this island is running.
  const enhanced = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );
  const pending = address !== current;

  const go = useCallback(
    (next: string) => {
      if (next === address) return;
      startTransition(() => {
        setAddress(next);
        router.push(next ? `?${next}` : pathname, { scroll: false });
      });
    },
    [address, pathname, router, setAddress],
  );

  useBlockEvents(ref, address, go);

  // The block's web components wait inside the hold until React has
  // hydrated their markup; the commit that drops the hold releases them.
  useEffect(() => {
    if (enhanced)
      ref.current?.dispatchEvent(new Event(INTERACTIVE_RELEASE_EVENT));
  }, [enhanced]);

  useEffect(() => {
    if (ref.current) showAddressInControls(ref.current);
  }, [current]);

  useEffect(() => {
    const results = ref.current?.querySelector(RESULTS_SELECTOR);
    results?.setAttribute("aria-busy", pending ? "true" : "false");
  }, [pending]);

  return (
    <div
      ref={ref}
      className={css.island}
      data-enhanced={enhanced ? "" : undefined}
      {...{ [INTERACTIVE_HOLD_ATTR]: enhanced ? undefined : "" }}
      data-pending={pending ? "" : undefined}
    >
      {children}
    </div>
  );
}
